import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS, PLAYS_API } from '@/constants/api-endpoints';
import {
  CalendarAccount,
  GetAvatarsResponse,
  GetHeygenVoicesResponse,
  ICampaignResults,
  ICreateAIAgentBody,
  ICreatedAIAgentItem,
  ICreateEmailTemplateBody,
  IDraggablePlayItem,
  IEmailDynamicVariableItem,
  IEmailTemplateItem,
  IPlayDetails,
  IPlayExecution,
  IPlayListingItem,
  IPlayPhoneNumberItem,
  IPlayResult,
  IPlayResultDetail,
  IStoreAudiencePLayData,
  GetAvatarGroupsResponse,
  GetAvatarsInGroupResponse,
  UploadAssetResponse,
} from '@/types/plays';
import type { RunHistoryResponse, RunDetailsResponse, RecentActivityResponse, HeyReachPendingStepLeadStatusResponse } from '@/types/execution-results';
import { apiInstance } from '@/utils';
import type { IViewProfileStepResult } from '@/utils/linkedinViewBeforeConnect';
import { startCase } from 'lodash';

export const getAllPlays = async (): Promise<IDraggablePlayItem[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAllPlays,
  });

  return result.data?.plays || [];
};

export const getPlayListings = async (): Promise<{
  playListings: IPlayListingItem[];
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getPlayListings,
  });

  return {
    playListings: result.data?.playListings || []
  };
};

export const getGroupedPlayListings = async (): Promise<{
  playListings: IPlayListingItem[];
  userGroups?: Array<{
    userId: string;
    userName: string;
    userEmail: string;
    totalTemplates: number;
    audienceTypes: Array<{
      type: string;
      count: number;
      templates: IPlayListingItem[];
    }>;
  }>;
  grouped?: boolean;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: `${API_ENDPOINTS.getPlayListings}?grouped=true`,
  });

  return {
    playListings: result.data?.playListings || [],
    userGroups: result.data?.userGroups || [],
    grouped: result.data?.grouped || false,
  };
};

export const storeAudiencePlay = async (
  data: IStoreAudiencePLayData,
): Promise<{
  audiencePlay: {
    audience_id: number;
    created_at: string;
    custom_action_details: string;
    id: number;
    name: string;
    play_id: number;
    user_id: string;
    vendorid: number;
  };
  // Present when the new node is a LinkedIn connection request: the server put a
  // View profile step and a 1-day Delay in front of it (or could not).
  viewProfileStep?: IViewProfileStepResult;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.storeAudiencePlay,
  });

  return result.data;
};

export const getPlayDetails = async (id: number): Promise<IPlayDetails> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getPlayDetails(id),
  });

  return result.data?.playDetails || {};
};

/**
 * Get workflow structure (plays) for a specific audience
 * Used for cloning workflows client-side
 */
export const getPlaysByAudienceId = async (audienceId: number): Promise<IPlayDetails> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getPlaysByAudienceId(audienceId),
  });

  return result.data?.playDetails || result.data || {};
};

export const updateAudiencePlay = async (id: number, data: IStoreAudiencePLayData) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.removeAudiencePlay(id),
    data,
  });

  return result.data?.playListings || [];
};

export const removeAudiencePlay = async (id: number) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.removeAudiencePlay(id),
  });

  return result.data?.playListings || [];
};

/**
 * Reparent a node via the dedicated move endpoint. This is the ONLY safe way
 * to restructure a workflow: the server reparents AND atomically re-derives
 * sequence_order from the tree. Using PUT /plays/{id} with parentNode is no
 * longer accepted (it silently corrupted execution order).
 */
export const moveAudiencePlay = async (
  id: number,
  parentNode: number | null,
): Promise<{ audiencePlay: { id: number; audience_id: number; parent_node: number | null }; workflowState: unknown }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.moveAudiencePlay(id),
    // The builder's drag-to-reconnect moves the node together with the steps
    // drawn under it. Without this flag the server moves only this one step
    // and splices it in after parentNode (what the chat agent uses to reorder).
    data: { parentNode, moveFollowingSteps: true },
  });

  return result.data;
};

export const removePlay = async ({
  playName,
  audienceId,
}: {
  playName: string;
  audienceId: number;
}) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    data: {
      playName,
    },
    url: API_ENDPOINTS.removePlay(audienceId),
  });

  return result.data?.playListings || [];
};

export const deleteCampaignFromAudience = async (
  audienceId: number,
): Promise<{ deletedPlaysCount: number; cancelledExecutionsCount: number; message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteCampaignFromAudience(audienceId),
  });

  return result.data;
};

export const checkPlayName = async (
  playName: string,
): Promise<{ message: string; exists: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.checkPlayName(playName),
  });

  return result.data;
};

export const createAIAgent = async (
  data: ICreateAIAgentBody,
): Promise<{ agent: { id: number } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.createAIAgent,
  });

  return result.data;
};

export const updateAIAgent = async ({ id, data }: { id: number; data: ICreateAIAgentBody }) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    data,
    url: API_ENDPOINTS.updateAIAgent(id),
  });

  return result.data;
};

export const getAIAgents = async (): Promise<{ agents: ICreatedAIAgentItem[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAIAgents,
  });

  return result.data;
};

export const getAIAgentById = async (id: number): Promise<{ agent: ICreatedAIAgentItem }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAIAgentById(id),
  });

  return result.data;
};

export const publishPlay = async (data: { audienceId: number; shouldPublish: boolean }) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    data,
    url: API_ENDPOINTS.publishPlay,
  });

  return result.data;
};

export const getPlaysPhoneNumber = async (): Promise<IPlayPhoneNumberItem[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getPlaysPhoneNumber,
  });

  return result.data;
};

export const getEmailDynamicVariables = async (
  audienceId?: number | string
): Promise<IEmailDynamicVariableItem[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getEmailDynamicVariables,
    // Scopes discovered custom-attribute variables (and their sample values) to this
    // audience's own list instead of every list the vendor has ever uploaded — otherwise
    // the picker offers fields that don't exist for the audience actually being edited.
    params: audienceId ? { audienceId } : undefined,
  });

  return result.data;
};

export const addPlaysPhoneNumber = async (data: {
  areaCode: string;
  cnamEnabled: boolean;
}): Promise<{ id: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.addPlaysPhoneNumber,
  });

  return result.data;
};

const normalizeStatus = (status?: string | null) => {
  if (!status) return 'pending';
  try {
    const parsed = typeof status === 'string' && status.startsWith('{') ? JSON.parse(status) : null;
    if (parsed?.state) return parsed.state;
    if (parsed?.status) return parsed.status;
  } catch {
    // fall through
  }
  return status;
};

const mapCampaignResultsToPlayResults = (
  apiData: Record<string, unknown>,
  params: { page?: number; pageSize?: number },
): IPlayResult => {
  const plays = (apiData?.plays || []) as Array<Record<string, unknown>>;

  const mapCrmResults = (outputData: Record<string, unknown> = {}): Array<Record<string, unknown>> => {
    const people: Array<Record<string, unknown>> = (outputData.people || []) as Array<Record<string, unknown>>;
    const companies: Array<Record<string, unknown>> = (outputData.companies || []) as Array<Record<string, unknown>>;
    const companyByName = new Map<string, Record<string, unknown>>();
    companies.forEach((c) => {
      const key = (c.name || c.Name || c.domain || c.Website) as string | undefined;
      if (key) companyByName.set(key, c);
    });
    return people.map((person) => ({
      contact: {
        name: [person.first_name, person.last_name].filter(Boolean).join(' ') || person.name || person.email || '',
        email: person.email || '',
        phone: person.phone || '',
        title: person.job_title || person.title || '',
        job_title: person.job_title || person.title || '',
        linkedin_url: person.linkedin_url || '',
        location: person.location || '',
        source: person.source || '',
        created_at: person.created_at,
        updated_at: person.updated_at,
      },
      company: {
        name: person.company_name || '',
        domain: person.website || '',
        industry: person.industry || '',
        logo: person.company_logo || '',
        location: person.company_location || { city: '', state: '', country: '', address: '' },
        _crm: person._company_sync || companyByName.get((person.company_name as string) || (person.website as string) || '') || {},
      },
      crm_data: {
        crm_type: (person._crm_sync as Record<string, unknown>)?.crm_type as string || '',
        crm_id: (person._crm_sync as Record<string, unknown>)?.crm_id || null,
        sync_status: (person._crm_sync as Record<string, unknown>)?.sync_status as string || '',
        synced_at: (person._crm_sync as Record<string, unknown>)?.synced_at as string || null,
        object_type: (person._crm_sync as Record<string, unknown>)?.object_type as string || '',
        pushed_fields: (person._crm_sync as Record<string, unknown>)?.pushed_fields as string[] || [],
        record_type: (person._crm_sync as Record<string, unknown>)?.object_type as string || '',
      },
    }));
  };

  const mapOutreachResults = (outputData: Record<string, unknown> = {}): Array<Record<string, unknown>> => {
    const calls: Array<Record<string, unknown>> = (outputData.calls || []) as Array<Record<string, unknown>>;
    const people: Array<Record<string, unknown>> = (outputData.people || []) as Array<Record<string, unknown>>;
    const results: Array<Record<string, unknown>> = (outputData.results || []) as Array<Record<string, unknown>>;

    const peopleById = new Map<number, Record<string, unknown>>();
    people.forEach((p) => peopleById.set(p.id as number, p));

    const resultsByCallId = new Map<string, Record<string, unknown>>();
    results.forEach((r) => {
      if (r.call_id) resultsByCallId.set(r.call_id as string, r);
    });

    return calls.map((call) => {
      const person = call.person_id ? peopleById.get(call.person_id as number) : null;
      const analysis = call.call_id ? resultsByCallId.get(call.call_id as string) : null;
      const durationSeconds =
        analysis?.duration_seconds ??
        call.call_duration_seconds ??
        call.duration ??
        person?.call_duration ??
        0;

      const recordingUrl =
        analysis?.recording_url ??
        person?.call_recording_url ??
        call.recording_url ??
        null;

      const recordingSid =
        analysis?.recording_sid ??
        person?.call_recording_sid ??
        call.call_sid ??
        null;

      // Normalize status for UI outcome: treat started/initiated as No Answer (execution already completed)
      let status = analysis?.status || call.status || 'unknown';
      if (status === 'started' || status === 'initiated') {
        status = 'No Answer';
      }

      const contact = {
        name: [person?.first_name, person?.last_name].filter(Boolean).join(' ') || person?.email || '',
        company: person?.company_name || '',
        phone: person?.phone || call.phone || '',
        email: person?.email || '',
        title: person?.job_title || '',
      };

      return {
        contact,
        call: {
          duration: durationSeconds,
          call_duration_seconds: durationSeconds,
          status,
          sentiment: (analysis?.analysis as Record<string, unknown>)?.user_sentiment as string || 'unknown',
          call_summary: (analysis?.analysis as Record<string, unknown>)?.call_summary as string || (analysis?.call_summary as string) || '',
          in_voicemail: false,
          completion_rating: 'unknown',
          task_completion: 'unknown',
          transcript: analysis?.transcript || '',
          recording_url: recordingUrl,
          recording_sid: recordingSid,
          call_id: call.call_id || analysis?.call_id || '',
          recording_duration: analysis?.recording_duration || durationSeconds,
          public_log_url: analysis?.public_log_url || '',
          direction: 'outbound',
          disconnection_reason: call.disconnection_reason || '',
          connected: analysis?.connected === true || status === 'Completed' || status === 'completed' || call.connected === true || (durationSeconds as number) > 0,
          mode: ((outputData?.meta as Record<string, unknown>)?.campaign_type === 'human_dialer' || (outputData?.meta as Record<string, unknown>)?.execution_mode === 'human_dialer')
            ? 'human_dialer'
            : 'ai_outreach',
          session_data: outputData?.meta
            ? {
              session_id: (outputData.meta as Record<string, unknown>).session_id,
              campaign_type: (outputData.meta as Record<string, unknown>).campaign_type,
              execution_mode: (outputData.meta as Record<string, unknown>).execution_mode,
            }
            : undefined,
          analysis: analysis?.analysis || undefined,
        },
      };
    });
  };

  const mapLinkedinResults = (outputData: Record<string, unknown> = {}): Array<Record<string, unknown>> => {
    const people: Array<Record<string, unknown>> = (outputData.people || []) as Array<Record<string, unknown>>;
    return people.map((person) => ({
      contact: {
        name: [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email || '',
        email: person.email || '',
        phone: person.phone || '',
        job_title: person.job_title || '',
        linkedin_url: person.linkedin_profile_url || person.linkedin_url || '',
      },
      company: {
        name: person.company_name || '',
        domain: person.website || '',
        industry: person.industry || '',
        location: person.company_location || '',
      },
      linkedin_profile: {
        occupation: person.job_title || '',
        connections: person.linkedin_connections || 0,
        follower_count: person.linkedin_followers || 0,
        profile_pic_url: person.linkedin_profile_picture_url || '',
        experiences: [],
        profile_url: person.linkedin_profile_url || person.linkedin_url || '',
        enrichment_note: person.enrichment_note || '',
        enrichment_source: person.enrichment_source || '',
      },
      status: person.linkedin_profile_url ? 'enriched' : 'not_found',
    }));
  };

  const flattenedPlays: IPlayResultDetail[] = plays.flatMap((play: Record<string, unknown>) => {
    const executions = (play?.executions || []) as Array<Record<string, unknown>>;

    return executions
      .map((execution: Record<string, unknown>) => {
        const executionId = execution.execution_id ?? execution.id;
        if (!executionId) return null;

        const outputData = (execution.output_data || {}) as Record<string, unknown>;
        let executionMetrics = execution.metrics || outputData.metrics || play.metrics || {};

        const derivedCrmType =
          (executionMetrics as Record<string, unknown>)?.crm_type ||
          ((outputData?.execution as Record<string, unknown>)?.source as Record<string, unknown>)?.type ||
          (outputData?.execution as Record<string, unknown>)?.agent_type ||
          null;

        if (derivedCrmType) {
          executionMetrics = { ...executionMetrics, crm_type: derivedCrmType };
        }

        // Build type-specific result payloads
        let derivedResults: Array<Record<string, unknown>> = [];
        if (play.play_type === 'email_campaign') {
          derivedResults = (execution.emails || (execution.results as Record<string, unknown>)?.emails || execution.results || []) as Array<Record<string, unknown>>;
        } else if (play.play_type === 'crm_add') {
          derivedResults = mapCrmResults(outputData as Record<string, unknown>);
        } else if (play.play_type === 'outreach_campaign') {
          derivedResults = mapOutreachResults(outputData as Record<string, unknown>);
        } else if (play.play_type === 'linkedin_enrichment') {
          derivedResults = mapLinkedinResults(outputData as Record<string, unknown>);
        } else {
          derivedResults = (execution.results || (outputData as Record<string, unknown>).results || []) as Array<Record<string, unknown>>;
        }

        const crmLabel = play.play_type === 'crm_add' && derivedCrmType
          ? `CRM (${startCase(String(derivedCrmType))})`
          : null;

        return {
          id: executionId,
          play_id: play.play_id,
          audience_play_id: play.audience_play_id,
          play_name: play.play_name,
          type: play.play_type,
          type_label: crmLabel || undefined,
          sequence: play.sequence_order ?? 0,
          status: normalizeStatus(execution.status as string | null | undefined) as IPlayResultDetail['status'],
          executed_at: execution.executed_at ?? execution.created_at ?? '',
          analytics: (play.metrics || executionMetrics || {}) as Record<string, unknown>,
          agent_type: ((execution.results as Record<string, unknown>)?.email as Record<string, unknown>)?.agent_type || (outputData?.execution as Record<string, unknown>)?.agent_type,
          contact: execution.contact,
          results: derivedResults,
          prefetched_execution: {
            metrics: executionMetrics,
            results: derivedResults,
            pagination: {
              total: Array.isArray(derivedResults) ? derivedResults.length : 0,
              totalPages: 1,
              page: 1,
              pageSize: Array.isArray(derivedResults) ? derivedResults.length : 0,
            },
            execution: {
              id: executionId as number,
              status: normalizeStatus(execution.status as string | null | undefined) as string,
              executed_at: (execution.executed_at ?? execution.created_at ?? null) as string | null,
              created_at: (execution.created_at ?? null) as string | null,
              is_test: (execution.test_mode ?? false) as boolean,
              play_type: play.play_type as string,
              agent_type: (((execution.results as Record<string, unknown>)?.email as Record<string, unknown>)?.agent_type ?? (outputData?.execution as Record<string, unknown>)?.agent_type ?? null) as string | null,
            },
            raw: outputData,
          } as unknown as Partial<IPlayExecution>,
        };
      })
      .filter(Boolean);
  }) as IPlayResultDetail[];

  const summary = (apiData?.summary || {}) as Record<string, unknown>;
  const total = (summary.total_executions as number) ?? flattenedPlays.length;
  const completed = (summary.completed as number) ?? flattenedPlays.filter((p) => p.status === 'completed').length;
  const inProgress = (summary.in_progress as number) ?? flattenedPlays.filter((p) => p.status === 'in_progress').length;
  const failed = (summary.failed as number) ?? flattenedPlays.filter((p) => p.status === 'failed').length;

  const overallStatus =
    total === 0
      ? 'pending'
      : failed > 0
        ? 'failed'
        : inProgress > 0
          ? 'in_progress'
          : 'completed';

  return {
    audience_id: ((apiData?.audience as Record<string, unknown>)?.id as string | number) ?? '',
    overall_status: overallStatus,
    plays: flattenedPlays,
    analytics: {
      total_completed: completed || 0,
      total_failed: failed || 0,
      total_in_progress: inProgress || 0,
    },
    pagination: {
      page: params?.page ?? 1,
      page_size: params?.pageSize ?? 10,
      total,
    },
  };
};

/**
 * Normalize execution data - handles both structures:
 * 1. Email campaigns: Already transformed by backend, has direct metrics/results
 * 2. Outreach/CRM: Raw data with output_data wrapper
 */
function normalizeExecution(execution: Record<string, unknown>, play: Record<string, unknown>): Record<string, unknown> {
  // Check if already normalized (email campaigns)
  const isNormalized = execution.metrics && !execution.output_data;

  if (isNormalized) {
    // Email campaign - already in correct format
    return {
      execution_id: execution.execution_id,
      status: execution.status,
      executed_at: execution.executed_at,
      test_mode: execution.test_mode || false,
      run_number: execution.run_number,
      sequence_order: execution.sequence_order,
      metrics: execution.metrics,
      results: execution.results,
    };
  }

  // Outreach/CRM campaign - extract from output_data
  const outputData = (execution.output_data || {}) as Record<string, unknown>;
  const metrics = (outputData.metrics || {}) as Record<string, unknown>;
  const agentMetrics = (metrics.agent_metrics || {}) as Record<string, unknown>;

  return {
    execution_id: execution.execution_id,
    status: normalizeStatus(execution.status as string | null | undefined) as string,
    executed_at: execution.executed_at as string,
    test_mode: (outputData.execution as Record<string, unknown>)?.test_mode as boolean || false,
    run_number: execution.run_number,
    sequence_order: play.sequence_order,
    metrics: {
      total_processed: metrics.total_processed || 0,
      successful: metrics.successful || 0,
      failed: metrics.failed || 0,
      // Flatten agent_metrics so UI components can read them directly
      ...agentMetrics,
      // Email-specific (will be undefined for non-email)
      emails_sent: metrics.emails_sent,
      emails_failed: metrics.emails_failed,
      emails_scheduled: metrics.emails_scheduled,
      emails_skipped_recent: metrics.emails_skipped_recent,
      emails_skipped_invalid: metrics.emails_skipped_invalid,
    },
    results: (outputData.results as unknown[]) || [],
    people: (outputData.people as unknown[]) || [],
    calls: (outputData.calls as unknown[]) || [],
    meta: outputData.meta,
  };
}

/**
 * Get campaign results using the new campaigns API endpoint
 * Returns hierarchical structure with plays → executions → results
 */
export const getCampaignResults = async (
  audienceId: number,
  params: {
    startDate?: string;
    endDate?: string;
    status?: 'completed' | 'failed' | 'in_progress' | 'pending';
    playTypes?: string[];
    includeMetrics?: boolean;
    includeResults?: boolean;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<ICampaignResults> => {
  const requestParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    status: params.status,
    playTypes: params.playTypes,
    includeMetrics: params.includeMetrics !== false, // default true
    includeResults: params.includeResults !== false, // default true
    page: params.page || 1,
    pageSize: params.pageSize || 10,
  };

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getPlayResults(audienceId),
    params: requestParams,
  });

  const data = result.data.data;

  // Normalize executions - handle both transformed (email) and raw (outreach/crm) structures
  const normalizedPlays = (data.plays as Array<Record<string, unknown>>).map((play: Record<string, unknown>) => ({
    ...play,
    executions: (play.executions as Array<Record<string, unknown>>).map((execution: Record<string, unknown>) =>
      normalizeExecution(execution, play),
    ),
  }));

  return {
    ...data,
    plays: normalizedPlays,
  };
};

/**
 * Legacy function - still using old mapping for backwards compatibility
 * TODO: Remove this once all components are migrated to getCampaignResults
 * @deprecated Use getCampaignResults instead
 */
export const getPlayResults = async (
  id: number,
  params: {
    playName?: string;
    playId?: number;
    page?: number;
    pageSize?: number;
    playStatus?: IPlayResultDetail['status'];
    playType?: IPlayResultDetail['type'];
  },
): Promise<IPlayResult> => {
  const { playType, playStatus, page, pageSize } = params || {};

  const requestParams = {
    page,
    pageSize,
    status: playStatus,
    playTypes: playType ? [playType] : undefined,
    includeMetrics: true,
    includeResults: true,
  };

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getPlayResults(id),
    params: requestParams,
  });

  return mapCampaignResultsToPlayResults(result.data?.data, requestParams);
};

export const getPlayExecution = async (
  id: number,
  params: {
    page?: number;
    pageSize?: number;
  },
): Promise<IPlayExecution> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getPlayExecution(id),
    params,
  });

  return result.data.data;
};

/**
 * Get audiences with recent play execution activity.
 * Powers the notification drawer's historical hydration.
 */
export const getRecentActivity = async (
  params: { hours?: number } = {},
): Promise<RecentActivityResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: PLAYS_API.getRecentActivity,
    params,
  });

  return result.data;
};

/**
 * Get run history for an audience
 * Lists all workflow runs grouped by execution_sequence
 */
export const getRunHistory = async (
  audienceId: number,
  params: {
    page?: number;
    pageSize?: number;
    startDate?: string;
    allHistory?: boolean;
  } = {},
): Promise<RunHistoryResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: PLAYS_API.getRunHistory(audienceId),
    params,
  });

  return result.data;
};

/**
 * Capture the newest run number before dispatching another run.
 *
 * The execution sheet uses this as an immutable baseline so a fast first step
 * cannot create the new run before the sheet's own post-dispatch refresh and
 * accidentally make that new run wait on itself. Zero means the audience has
 * no prior run history.
 */
export const getLatestRunNumber = async (audienceId: number): Promise<number> => {
  const history = await getRunHistory(audienceId, { page: 1, pageSize: 1 });
  return history.data?.runs?.[0]?.run_number ?? 0;
};

/**
 * Get detailed results for a specific run
 * Returns all play executions for a given run number
 */
export const getRunDetails = async (
  audienceId: number,
  runNumber: number,
): Promise<RunDetailsResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: PLAYS_API.getRunDetails(audienceId, runNumber),
  });

  return result.data;
};

export const resumeExecutionSafely = async (
  executionId: number,
): Promise<{ success: boolean; data?: { execution_id: number; status: string; record_count: number; message: string } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: PLAYS_API.resumeExecutionSafely(executionId),
  });
  return result.data;
};

/**
 * Per-lead HeyReach status for a step that hasn't executed yet — what each
 * recipient is actually doing while the step shows "waiting" (e.g. behind the
 * LinkedIn connection-acceptance gate).
 */
export const getHeyReachPendingStepLeadStatus = async (
  audienceId: number,
  runNumber: number,
  audiencePlayId: number,
): Promise<HeyReachPendingStepLeadStatusResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: PLAYS_API.getHeyReachPendingStepLeadStatus(audienceId, runNumber, audiencePlayId),
  });

  return result.data;
};

/**
 * Transform API result item to match frontend ResultItem type
 * Handles the nested structure from the API and flattens it
 */
function transformResultItem(
  item: Record<string, unknown>,
  playType: string,
): Record<string, unknown> {
  const contact = item.contact as Record<string, unknown> || {};
  const company = item.company as Record<string, unknown> || {};
  const companyLocation = company.location as Record<string, unknown> | string | null;

  // Base structure
  const result: Record<string, unknown> = {
    contact: {
      name: contact.name || '',
      title: contact.title || '',
      email: contact.email || null,
      phone: contact.phone || null,
      company: contact.company || '',
      location: contact.location || null,
      linkedin_url: contact.linkedin_url || null,
    },
    company: {
      name: company.name || '',
      domain: company.domain || '',
      industry: company.industry || '',
      logo: company.logo || null,
      // Handle location as object or string
      location: typeof companyLocation === 'object' && companyLocation
        ? (companyLocation.city || companyLocation.country || null)
        : companyLocation,
    },
  };

  // Add play-type specific fields
  if (playType === 'email_campaign') {
    const email = item.email as Record<string, unknown> || {};
    const reply = email.reply as Record<string, unknown> | null;

    result.type = 'email';
    result.email_status = normalizeEmailStatus(email.status as string);
    result.subject = email.subject || '';
    result.sent_at = email.sent_at || null;
    result.opens_count = email.opens_count || 0;
    result.clicks_count = email.clicks_count || 0;
    result.reply = reply ? {
      has_reply: reply.has_reply || false,
      received_at: reply.replied_at || reply.received_at,
      sentiment: reply.sentiment || null,
    } : null;
    // Include full email object for detail view
    result._email = email;
  } else if (playType === 'outreach_campaign') {
    const call = item.call as Record<string, unknown> || item;
    result.type = 'outreach';
    result.call_status = call.status || call.call_status || 'unknown';
    result.connected = call.connected || false;
    result.duration_seconds = call.duration_seconds || call.duration || 0;
    result.recording_url = call.recording_url || null;
    result.transcript = call.transcript || null;
    result.analysis = call.analysis || null;
  } else if (playType === 'crm_add') {
    result.type = 'crm';
    result.sync_status = item.sync_status || item.status || 'unknown';
    result.crm_type = item.crm_type || null;
    result.crm_record_id = item.crm_record_id || item.record_id || null;
    result.synced_fields = item.synced_fields || [];
    result.qualification = item.qualification || null;
  } else if (playType === 'linkedin_enrichment') {
    result.type = 'linkedin';
    result.enrichment_status = item.enrichment_status || item.status || 'unknown';
    result.enrichment_source = item.enrichment_source || null;
    result.linkedin_url = item.linkedin_url || contact.linkedin_url || null;
  } else if (playType === 'heyreach_campaign') {
    result.type = 'heyreach';
    result.status = item.status || 'unknown';
    result.linkedin_url = item.linkedin_url || contact.linkedin_url || null;
    result.message_content = item.message_content || null;
  } else {
    // Generic fallback
    result.type = 'generic';
    result.status = item.status || 'unknown';
    result.metadata = item;
  }

  return result;
}

/**
 * Normalize email status to expected values
 */
function normalizeEmailStatus(status: string | undefined): string {
  if (!status) return 'unknown';
  if (status.startsWith('skipped')) return 'skipped';
  if (status === 'sent' || status === 'replied') return 'sent';
  if (status === 'failed') return 'failed';
  if (status === 'scheduled' || status === 'pending') return 'scheduled';
  return status;
}

/**
 * Get execution details with formatted items
 * Used by the new Results panel slide-in
 */
const getExecutionDetails = async (
  executionId: number,
): Promise<{ success: boolean; data?: { items: unknown[] }; error?: string }> => {
  try {
    const result = await apiInstance({
      method: REQUEST_METHOD.GET,
      url: API_ENDPOINTS.getPlayExecution(executionId),
      params: { page: 1, pageSize: 500 },
    });

    // Transform the response to match the new format
    const data = result.data?.data;
    const rawItems = data?.results || [];
    const playType = data?.execution?.play_type || data?.execution?.agent_type || 'generic';

    // Transform each item to match frontend types
    const items = (rawItems as Record<string, unknown>[]).map((item) =>
      transformResultItem(item, playType),
    );

    return {
      success: true,
      data: { items },
    };
  } catch {
    return {
      success: false,
      error: 'Failed to fetch execution details',
    };
  }
};

export const createEmailTemplate = async (
  data: ICreateEmailTemplateBody,
  category: 'email' | 'video',
): Promise<{ id: number; name: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    params: {
      category,
    },
    url: API_ENDPOINTS.createEmailTemplate,
  });

  return result.data;
};

export const updateEmailTemplate = async ({
  id,
  data,
}: {
  data: ICreateEmailTemplateBody;
  id: number;
}) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    data,
    url: API_ENDPOINTS.updateEmailTemplate(id),
  });

  return result.data;
};

export const getEmailTemplates = async (
  category: 'email' | 'video',
): Promise<IEmailTemplateItem[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params: {
      category,
    },
    url: API_ENDPOINTS.getEmailTemplates,
  });

  return result.data;
};

export interface UploadPlayImageOptions {
  file: File;
  signal?: AbortSignal;
  onUploadProgress?: (percent: number) => void;
}

export const uploadPlayImage = async (
  input: File | UploadPlayImageOptions,
): Promise<{ url: string; cloudflareId: string }> => {
  const options = input instanceof File ? null : input;
  const file = input instanceof File ? input : input.file;
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: formData,
    url: API_ENDPOINTS.uploadPlayImage,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    signal: options?.signal,
    onUploadProgress: options?.onUploadProgress
      ? (event) => {
          const total = event.total || file.size;
          const percent = total > 0
            ? Math.min(95, Math.round((event.loaded / total) * 100))
            : 0;
          options.onUploadProgress?.(percent);
        }
      : undefined,
  });

  return result.data;
};

export const uploadPlayAudio = async (
  file: File,
): Promise<{ url: string; cloudflareId: string; file_type: string; storage: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: formData,
    url: API_ENDPOINTS.uploadPlayAudio,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return result.data;
};

export const getAvatars = async (): Promise<GetAvatarsResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.heygenAvatars,
  });
  return result.data;
};

export const getHeygenVoices = async (): Promise<GetHeygenVoicesResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.heygenVoices,
  });
  return result.data;
};

export const getAllCalendarsByUser = async (): Promise<CalendarAccount[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAllCalendarsByUser,
  });
  return result.data;
};

export const deleteAIAgent = async (id: number) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteAIAgent(id),
  });

  return result.data;
};

export const getAvatarGroups = async (): Promise<GetAvatarGroupsResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.heygenAvatarGroups,
  });
  return result.data;
};

export const getAvatarsInGroup = async (groupId: string): Promise<GetAvatarsInGroupResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: `${API_ENDPOINTS.heygenAvatarsInGroup}/${groupId}/avatars`,
  });
  return result.data;
};

export const uploadAssetToHeyGen = async (file: File): Promise<UploadAssetResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: formData,
    url: API_ENDPOINTS.heygenUploadAsset,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return result.data;
};

/**
 * PATCH a workflow node and return the server's whole answer: the node, plus `viewProfileStep`
 * when the save turned it into a LinkedIn connection request and the server put a View profile
 * step and a 1-day Delay in front of it (which changes the workflow's structure).
 */
export const patchAudiencePlayNodeWithResult = async (
  nodeId: number,
  updates: {
    title?: string;
    description?: string;
    customActionDetails?: Record<string, unknown>;
  },
): Promise<{ node: Record<string, unknown>; viewProfileStep?: IViewProfileStepResult }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.patchPlayNode(nodeId),
    data: updates,
  });

  return { node: result.data.node, viewProfileStep: result.data.viewProfileStep };
};

export const patchAudiencePlayNode = async (
  nodeId: number,
  updates: {
    title?: string;
    description?: string;
    customActionDetails?: Record<string, unknown>;
  },
) => (await patchAudiencePlayNodeWithResult(nodeId, updates)).node;

// SDR Sequence Control API (#738, #739)

export const skipPlay = async (
  audiencePlayId: number,
  data?: { reason?: string },
) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.skipPlay(audiencePlayId),
    data,
  });
  return result.data;
};

/**
 * Send now (mb-frontend#4913): release the wait holding this sequence and send its next message
 * step immediately. A repeat call sends nothing and reports `alreadyReleased`.
 */
export const sendNextStepNow = async (audienceId: number) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.sendNextStepNow(audienceId),
  });
  return result.data as {
    success: boolean;
    data: { released: boolean; alreadyReleased?: boolean; unconfirmed?: boolean; targetPlayId?: number; stepType?: string };
  };
};

export const pauseSequence = async (audienceId: number) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.pauseSequence(audienceId),
  });
  return result.data;
};

export const resumeSequence = async (audienceId: number) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.resumeSequence(audienceId),
  });
  return result.data;
};

export interface CancelSequenceResponse {
  success: boolean;
  message: string;
  data: {
    cancelled_count: number;
    /** Email stops at once; a LinkedIn campaign has to be paused on HeyReach, which can fail. */
    linkedin?: { failed?: unknown[]; error?: string };
  };
}

export const cancelSequence = async (audienceId: number): Promise<CancelSequenceResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.cancelSequence(audienceId),
  });
  return result.data;
};

export const bulkCancelSequences = async (audienceIds: number[]) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.bulkCancelSequences,
    data: { audienceIds },
  });
  return result.data as {
    success: boolean;
    message: string;
    data: {
      total: number;
      succeeded: number;
      failed: number;
      results: Array<{ audienceId: number; cancelled_count?: number; error?: string }>;
    };
  };
};

/**
 * Per-workflow outcome of stopping companies: a workflow made up only of those companies is
 * cancelled (`cancelled_count` steps); a shared one keeps running and only the companies'
 * contacts are stopped (`stopped_contacts`).
 */
export type CompanyStopResult = {
  audienceId: number;
  cancelled_count?: number;
  stopped_contacts?: number;
  error?: string;
};

export const bulkCancelByCompany = async (companyDomains: string[]) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.bulkCancelByCompany,
    data: { companyDomains },
  });
  return result.data as {
    success: boolean;
    message: string;
    data: {
      total: number;
      succeeded: number;
      failed: number;
      results: CompanyStopResult[];
      domains: string[];
    };
  };
};

/**
 * GH-1029: Fetch full email thread for a sequence
 */
export const getEmailThread = async (
  sequenceId: string | number,
): Promise<{
  thread: {
    id: number;
    thread_id: string;
    prospect_email: string;
    message_count: number;
    inbound_count: number;
    outbound_count: number;
    status: string;
    first_message_at: string;
    last_message_at: string;
  } | null;
  messages: Array<{
    id: number;
    direction: 'inbound' | 'outbound';
    email_from: string;
    email_to: string;
    subject: string;
    body_html: string | null;
    body_text: string | null;
    sent_at: string;
    message_order: number;
    is_campaign?: boolean;
  }>;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getEmailThread(sequenceId),
  });

  return result.data;
};

/**
 * Where a copied workflow lands.
 *
 * - `copy_people` — clone the source audience *with* its people. The server default, and
 *   what every caller did before this field existed.
 * - `empty` — clone the definition without any people, so a different list can be uploaded
 *   into the copy. The response carries `listId`/`type` for exactly that import.
 * - `existing` — no audience is created; the workflow is copied into `targetAudienceId`,
 *   which must carry no campaign of its own (one audience holds at most one workflow).
 */
export type CopyWorkflowAudienceMode = 'copy_people' | 'empty' | 'existing';

export interface CopyWorkflowWithAudienceRequest {
  sourceAudienceId: number;
  audienceMode?: CopyWorkflowAudienceMode;
  /** Required by `copy_people` and `empty`. For `existing` it labels the target's campaign_name. */
  newAudienceName?: string;
  /** Required by `existing`, rejected by the other modes. */
  targetAudienceId?: number;
}

export interface CopyWorkflowWithAudienceResponse {
  message: string;
  audienceId: number;
  audienceName?: string;
  copiedPlaysCount: number;
  /** Absent for `existing`; null when the new audience is rule-backed rather than list-backed. */
  listId?: number | null;
  /** Absent for `existing`. */
  type?: 'person' | 'company' | null;
  /** Absent for `existing`; always 0 for `empty`. */
  memberCount?: number;
}

export const copyWorkflowWithAudience = async (
  data: CopyWorkflowWithAudienceRequest,
): Promise<CopyWorkflowWithAudienceResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.copyWorkflowWithAudience,
    data,
  });

  return result.data;
};

/**
 * Copy a system template's plays into an EXISTING audience (1:1 model).
 *
 * Unlike copyWorkflowWithAudience (which duplicates the source audience and
 * attaches the workflow to the clone), this endpoint attaches the template's
 * play structure directly to the already-selected target audience. Required
 * for the split Campaigns flow so the campaign actually lands on the
 * audience the user picked in CreateCampaignModal — no orphan duplicate.
 *
 * Target must not already have an active workflow; backend returns 400 with
 * "already has an active workflow" if it does.
 */
export const copyTemplateToAudience = async (data: {
  sourceAudienceId: number;
  targetAudienceId: number;
}): Promise<{
  message: string;
  audienceId: number;
  copiedPlaysCount: number;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.copyTemplateToAudience,
    data,
  });

  return result.data;
};
